import * as React from "react";
import { useEffect, useState } from "react";
import { Flex, Spinner, Text } from "@radix-ui/themes";
import { Authenticated, AuthLoading, Unauthenticated, useMutation } from "convex/react";
import { api } from "@repo/convex/convex/_generated/api";
import { AuthShell } from "../auth/AuthShell.tsx";
import { SignIn } from "../auth/SignIn.tsx";
import { API_TOKEN_MESSAGE, type ApiTokenMessage } from "@repo/shared/extension/messages.ts";

// The Chrome extension can't run Apple's or Google's sign-in itself, so its Sign in button opens
// this page. Once you're signed in here it makes (or fetches) your extension API token and hands
// it straight to the extension, which keeps it in chrome.storage.sync like it always has.

// The Chrome Web Store's StashIt (docs/chrome-web-store.md). A dev build of this page (the local
// dev server) talks to whichever extension opened it instead, so an unpacked one can be tested.
const PUBLISHED_EXTENSION_ID = "ajpgmbceelggliacibeaejkfchchhief";

type ChromeRuntime = {
  sendMessage: (
    extensionId: string,
    message: unknown,
    callback: (response: unknown) => void,
  ) => void;
  lastError?: { message?: string };
};

// Chrome only gives a page `chrome.runtime` when an installed extension lists the page's site
// in its `externally_connectable`
const chromeRuntime = () =>
  (globalThis as { chrome?: { runtime?: ChromeRuntime } }).chrome?.runtime;

const sendToExtension = (extensionId: string, token: string) =>
  new Promise<void>((resolve, reject) => {
    const runtime = chromeRuntime();
    if (!runtime?.sendMessage) return reject(new Error("no extension"));
    const message: ApiTokenMessage = { type: API_TOKEN_MESSAGE, token };
    runtime.sendMessage(extensionId, message, (response) => {
      if (runtime.lastError || (response as { ok?: boolean } | undefined)?.ok !== true)
        return reject(new Error(runtime.lastError?.message ?? "the extension didn't answer"));
      resolve();
    });
  });

interface Props {
  extensionId?: string;
}

export const ExtensionPage: React.FC<Props> = ({ extensionId }) => {
  const target = (import.meta.env.DEV && extensionId) || PUBLISHED_EXTENSION_ID;
  return (
    <AuthShell intro="Connect the Chrome extension, so one click stashes the page you're on.">
      <Unauthenticated>
        <SignIn />
      </Unauthenticated>
      <Authenticated>
        <ConnectExtension extensionId={target} />
      </Authenticated>
      <AuthLoading>
        <Flex justify="center">
          <Spinner size={"3"} />
        </Flex>
      </AuthLoading>
    </AuthShell>
  );
};

const ConnectExtension: React.FC<{ extensionId: string }> = ({ extensionId }) => {
  const createOrGetExtensionToken = useMutation(api.apiTokens.createOrGetExtensionToken);
  const [state, setState] = useState<"connecting" | "connected" | "failed">("connecting");

  useEffect(() => {
    let cancelled = false;
    createOrGetExtensionToken()
      .then((token) => sendToExtension(extensionId, token))
      .then(() => !cancelled && setState("connected"))
      .catch((e) => {
        console.error("Couldn't connect the extension", e);
        if (!cancelled) setState("failed");
      });
    return () => {
      cancelled = true;
    };
  }, [extensionId]);

  if (state == "connecting")
    return (
      <Flex gap={"2"} align={"center"} justify={"center"}>
        <Spinner /> <Text>Connecting the extension…</Text>
      </Flex>
    );
  if (state == "connected")
    return (
      <Text align={"center"} as={"p"}>
        All set. Close this tab and click StashIt again to stash the page you were on.
      </Text>
    );
  return (
    <Text align={"center"} as={"p"}>
      Couldn't reach the StashIt extension. Make sure it's installed in this browser, then click its
      Sign in button again.
    </Text>
  );
};
